
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import './PlacePicker.css';
import Location from '@/models/location';
import type { Location as LocationModel } from '@/models/locationModel';
import Post from '@/models/post';
import type PostLocation from '@/models/postLocation';
import {
    getLocationAutocomplete,
    LocationAutocompleteParams,
    dedupeLocations,
    parseLocationsFromText,
    searchLocationByName,
    mergeLocationResults,
} from '@/api/location';
import { fetchAllUserPosts, fetchSavedPostsforUser } from '@/api/auth';
import { getPostById } from '@/api/itinerary';
import { buildLocationFromFeedItem, fetchSavedLocationsForUser } from '@/api/locationSaves';
import { getCityCoordinates } from '@/utils/cityCoordinates';
import { onlyLocationsInCountry } from '@/utils/locationCountry';
import { extractAllPostPhotos } from '@/utils/postPhotoExtraction';
import CustomImage from '@/components/CustomImage/CustomImage';
import type { CityFormData } from '@/types/city';
import PlaceSearchResults from '@/components/PlaceSearchAutocomplete/PlaceSearchResults';
import PlaceSearchResultCard from '@/components/PlaceSearchAutocomplete/PlaceSearchResultCard';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import CombinedContentPlaceModal from '@/components/CombinedContentPlaceModal/CombinedContentPlaceModal';
import { useAuth } from '@/contexts';
import { debug, error as logError, log } from '@/utils/logger';
import { getPostDestinationLabel } from '@/utils/postPresentationNormalizer';
import { WebAnalytics } from '@/lib/analytics';
import { isMobileClient } from '@/lib/platform';
import {
    EVENT_LOCATION_AUTOCOMPLETE_REQUEST,
    EVENT_LOCATION_AUTOCOMPLETE_SUCCESS,
    EVENT_LOCATION_AUTOCOMPLETE_EMPTY,
    EVENT_LOCATION_AUTOCOMPLETE_FAILED,
    EVENT_INSPO_LINK_SUBMITTED,
} from '@/lib/analyticsEvents';
import { requestItineraryImport } from '@/api/itineraryImport';
import type {
  ItineraryImportDraft,
  ItineraryImportDraftPlace,
  ItineraryImportSourceType,
} from '@/models/itineraryImportTypes';
import { socialScraperService } from '@/services/socialScraperService';
import { socialScraperEntryToLocation } from '@/utils/socialScraperTransform';
import { defaultSelectedPlaceIds } from '@/utils/itineraryImportBuilder';
import { buildSocialItineraryImportSource } from '@/utils/socialItineraryImport';
import { mapDraftPlaceToReviewPlace } from '@/utils/tripImportReviewPlaces';
import { isTravelImportPlace } from '@/utils/tripImportReviewFilters';
import { PlaceSearchRowTypeIcon } from '@/components/PlaceSearchAutocomplete/PlaceSearchRowTypeIcon';
import { validateSocialMediaUrl } from '@/utils/urlValidation';
import { detectSocialPlatform } from '@/utils/platformDetection';
import SVGTikTokIcon from '@/components/svgs/SVGTikTokIcon';
import SVGInstagramIcon from '@/components/svgs/SVGInstagramIcon';
import { DEFAULT_POST_IMAGE } from '@/constants/common';
import Button from '@/components/Buttons/Button/Button';
import { toast } from 'react-toastify';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlacePickerBatchListSection from '@/components/PlacePicker/PlacePickerBatchListSection';
import {
    SocialParseAnimation,
    SOCIAL_PARSE_RIVE_ASSET_URL,
    preloadRiveAsset,
} from '@/components/RiveParseAnimation/RiveParseAnimation';

export type PlacePickerTab = 'search' | 'my-trips' | 'saves' | 'social' | 'copy-paste';

/** Source of the selected place for analytics */
export type PlacePickerSelectSource = 'search' | 'my-trips' | 'saves';

export interface PlacePickerProps {
    /** Called when a place is selected. Second arg is source for Recommend a Place analytics. */
    onSelectPlace: (location: Location, source?: PlacePickerSelectSource) => void;
    /** Optional city for search location biasing */
    city?: CityFormData | null;
    /**
     * Only show search results in this country (a list scoped to a whole country). When there's
     * no city it also gives the text search the country as context.
     */
    country?: string | null;
    /** Optional className for the root element */
    className?: string;
    /** Initial tab to show (default Saved) */
    initialTab?: PlacePickerTab;
    /** Context label shown in the search empty state */
    contextLabel?: string;
    /** Whether to show the "Add your own place" link in Search tab */
    onCustomCardClick?: () => void;
    /** Label for the search/explore tab (default "Search") */
    searchTabLabel?: string;
    /** Show Copy & Paste tab (parse list text → multi-select, list flows only) */
    showCopyPasteTab?: boolean;
    /** Show Social tab (Inspo → Trip scraper: Instagram/TikTok link → multi-select) */
    showSocialTab?: boolean;
    /** Called when user confirms a batch of selected places (Explore, Copy & Paste, Social, Saved/My Trips drill-down). */
    onCopyPasteConfirm?: (locations: Location[]) => void | Promise<void>;
    /** Social tab: append structured import draft (notes, custom rows) onto open trip/list. */
    onSocialImportDraftConfirm?: (params: {
        draft: ItineraryImportDraft;
        selectedClientIds: Set<string>;
        importId: string;
        importStartedAt: string;
        sourceType: ItineraryImportSourceType;
    }) => void | Promise<void>;
    /**
     * When true with onCopyPasteConfirm, Explore tab uses + multi-select and the batch bar.
     * When false, Explore adds on row tap (onSelectPlace). Saved/My Trips drill-down, Copy/Paste, and Social
     * still use multi-select whenever onCopyPasteConfirm is set. Default false.
     */
    exploreUsesBatchSelect?: boolean;
    /** Hide Saved / My Trips / other tabs and show search only. */
    searchOnly?: boolean;
    /** Pre-fill the search box and run a search when long enough. */
    initialSearchQuery?: string;
}

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_DELAY = 500;
const FETCH_TIMEOUT = 15000;

/** Stable row key for multi-select (Copy & Paste, Social). */
const getResultRowKey = (loc: Location, index: number): string =>
    String(loc._id || loc.google_place_id || `paste-${index}`);

/**
 * Explore selection identity. Unlike the other tabs, an Explore selection outlives the result set
 * that produced it (each place needs its own query), so the key must not depend on row position.
 *
 * `google_place_id` comes first — the same place can arrive as a Google-only hit (no `_id`) under
 * one query and as a DB doc (both ids) under another, and `mergeLocationResults` already dedupes
 * google-id-first. Keying on `_id` first would let one place be selected twice.
 *
 * The `run-` fallback only covers rows the API returned with no identity at all; it intentionally
 * cannot match across queries, so such a row can never be confused with a different place.
 */
export const getExploreRowKey = (loc: Location, index: number, searchRunId: number): string =>
    String(loc.google_place_id || loc._id || `run-${searchRunId}-${index}`);

/**
 * Social scraper can repeat the same place id; include index so React keys and selection state
 * stay one-to-one with rows (duplicate getResultRowKey breaks list + multi-select).
 */
const getSocialBatchRowKey = (loc: Location, index: number): string =>
    `${getResultRowKey(loc, index)}#${index}`;

/** Row key for Saved / My Trips drill-down (prefer postLocation id). */
const SAVED_PLACES_PAGE_SIZE = 50;
const SAVED_PLACES_MAX_PAGES = 20;

/** Every place the user saved on its own, as the Location the picker selects. */
async function fetchAllSavedPlaces(): Promise<Location[]> {
    const places: Location[] = [];
    for (let page = 0; page < SAVED_PLACES_MAX_PAGES; page += 1) {
        const items = await fetchSavedLocationsForUser({
            limit: SAVED_PLACES_PAGE_SIZE,
            skip: page * SAVED_PLACES_PAGE_SIZE,
        });
        items.forEach((item) => {
            const loc = buildLocationFromFeedItem(item) as Location;
            const hasIdentity =
                (typeof loc._id === 'string' && loc._id.length > 0) ||
                (typeof loc.google_place_id === 'string' && loc.google_place_id.length > 0);
            if (hasIdentity) places.push(loc);
        });
        if (items.length < SAVED_PLACES_PAGE_SIZE) break;
    }
    return places;
}

const getSavedPlaceRowKey = (loc: Location, index: number): string =>
    `saved-place-${loc._id || loc.google_place_id || ''}-${index}`;

const getDrillDownRowKey = (pl: PostLocation, mergedLoc: Location, index: number): string =>
    pl._id ? String(pl._id) : getResultRowKey(mergedLoc, index);

const getPostImages = (post: Post): string[] => {
    const photos = extractAllPostPhotos(post);
    return photos.length > 0 ? photos : [DEFAULT_POST_IMAGE];
};

const getPostSubtitle = (post: Post): string => {
    const city = getPostDestinationLabel(post);
    const type = post.post_type || '';
    if (city && city !== 'Unknown Location' && type) return `${type} · ${city}`;
    return (city !== 'Unknown Location' ? city : '') || type || '';
};

type BatchAddDestination = 'list' | 'trip';

/** Visible + screen-reader labels for batch add confirm (primary CTA). */
const getBatchAddLabels = (
    count: number,
    destination: BatchAddDestination
): { label: string; ariaLabel: string } => {
    const dest = destination === 'list' ? 'list' : 'trip';
    const destPhrase = destination === 'list' ? 'your list' : 'your trip';
    if (count <= 0) {
        return {
            label: 'Select places first',
            ariaLabel: `Choose one or more places with the plus buttons, then use this button to add them to ${destPhrase}`,
        };
    }
    if (count === 1) {
        return {
            label: `Add 1 place to ${dest}`,
            ariaLabel: `Add the selected place to ${destPhrase}`,
        };
    }
    return {
        label: `Add ${count} places to ${dest}`,
        ariaLabel: `Add ${count} selected places to ${destPhrase}`,
    };
};

const getBatchSelectHint = (destination: BatchAddDestination): string => {
    const destPhrase = destination === 'list' ? 'your list' : 'your trip';
    return `Use + to select places, then tap the orange button below to add them to ${destPhrase}.`;
};

/** Explore only: selections survive a new query, so say so while the results area is empty. */
const getExplorePendingHint = (count: number): string =>
    count === 1
        ? 'Search again to keep adding — your selected place stays here.'
        : `Search again to keep adding — your ${count} selected places stay here.`;

const PlacePicker: React.FC<PlacePickerProps> = ({
    onSelectPlace,
    city,
    country,
    className = '',
    initialTab = 'saves',
    contextLabel,
    onCustomCardClick,
    searchTabLabel = 'Search',
    showCopyPasteTab = false,
    showSocialTab = false,
    onCopyPasteConfirm,
    onSocialImportDraftConfirm,
    exploreUsesBatchSelect = false,
    searchOnly = false,
    initialSearchQuery = '',
}) => {
    /** Parent passed batch confirm (e.g. list modal): Saved drill-down, Copy/Paste, Social use multi-select + bar. */
    const batchSelectionEnabled = Boolean(onCopyPasteConfirm);
    /** Explore tab only: multi-select vs single-tap add. */
    const exploreBatchMode = batchSelectionEnabled && exploreUsesBatchSelect;
    const batchDestination: BatchAddDestination = contextLabel === 'list' ? 'list' : 'trip';
    const { user } = useAuth();

    const resolvedInitialTab: PlacePickerTab = (() => {
        if (initialTab === 'copy-paste' && !showCopyPasteTab) return 'saves';
        if (initialTab === 'social' && !showSocialTab) return 'saves';
        return initialTab;
    })();

    // ── Tab ───────────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState<PlacePickerTab>(
        searchOnly ? 'search' : resolvedInitialTab,
    );

    // ── Search state ──────────────────────────────────────────────────────────
    const [searchInput, setSearchInput] = useState('');
    const [searchResults, setSearchResults] = useState<Location[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchHasError, setSearchHasError] = useState(false);
    const [searchErrorMessage, setSearchErrorMessage] = useState('');
    const [searchAttempted, setSearchAttempted] = useState(false);
    const [focusedResultIndex, setFocusedResultIndex] = useState(-1);
    /** Bumped per completed search so identity-less rows get a key that can't collide across queries. */
    const [searchRunId, setSearchRunId] = useState(0);
    const abortControllerRef = useRef<AbortController | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const seededQueryRef = useRef(false);

    // ── My Trips state ────────────────────────────────────────────────────────
    const [myTripsPosts, setMyTripsPosts] = useState<Post[]>([]);
    const [myTripsLoading, setMyTripsLoading] = useState(false);
    const [myTripsError, setMyTripsError] = useState('');

    // ── Saves state ───────────────────────────────────────────────────────────
    const [savesPosts, setSavesPosts] = useState<Post[]>([]);
    /** Places saved on their own (bookmarked), listed above saved trips & lists. */
    const [savedPlaces, setSavedPlaces] = useState<Location[]>([]);
    const [savedPlaceSelectedKeys, setSavedPlaceSelectedKeys] = useState<Set<string>>(new Set());
    const [savesLoading, setSavesLoading] = useState(false);
    const [savesError, setSavesError] = useState('');

    // ── Copy & Paste (list add-from-text, same API as list creation wizard) ──
    const [copyPasteText, setCopyPasteText] = useState('');
    const [copyPasteLoading, setCopyPasteLoading] = useState(false);
    const [copyPasteError, setCopyPasteError] = useState('');
    const [copyPasteResults, setCopyPasteResults] = useState<Location[]>([]);
    const [copyPasteSelectedKeys, setCopyPasteSelectedKeys] = useState<Set<string>>(new Set());
    const [copyPasteControlsCollapsed, setCopyPasteControlsCollapsed] = useState(false);

    // ── Explore multi-select ──────────────────────────────────────────────────
    // Unlike the other batch tabs, this selection PERSISTS across searches: on Explore each place
    // needs its own query, so a selection tied to the current result set could never hold more
    // than one place. Holding the Locations themselves also means confirm never has to re-derive
    // them from `searchResults`, which no longer contains the earlier picks.
    const [exploreSelected, setExploreSelected] = useState<Map<string, Location>>(() => new Map());
    /** Row-highlight lookup for PlaceSearchResults (unchanged prop contract). */
    const exploreSelectedKeys = useMemo(() => new Set(exploreSelected.keys()), [exploreSelected]);

    // ── Saved / My Trips drill-down multi-select (batch when onCopyPasteConfirm) ─
    const [drillDownSelectedKeys, setDrillDownSelectedKeys] = useState<Set<string>>(new Set());

    // ── Social (Inspo scraper — same batch confirm as Copy & Paste) ───────────
    const [socialLink, setSocialLink] = useState('');
    const [socialLoading, setSocialLoading] = useState(false);
    const [socialError, setSocialError] = useState('');
    const [socialResults, setSocialResults] = useState<Location[]>([]);
    const [socialSelectedKeys, setSocialSelectedKeys] = useState<Set<string>>(new Set());
    const [socialControlsCollapsed, setSocialControlsCollapsed] = useState(false);
    const [socialDraft, setSocialDraft] = useState<ItineraryImportDraft | null>(null);
    const [socialDraftSelectedIds, setSocialDraftSelectedIds] = useState<Set<string>>(new Set());
    const [socialImportSession, setSocialImportSession] = useState<{
        importId: string;
        importStartedAt: string;
        sourceType: ItineraryImportSourceType;
    } | null>(null);

    // Warm the Import Inspo parse animation so it plays immediately on "Find places".
    useEffect(() => {
        if (activeTab !== 'social' || !showSocialTab) return;
        preloadRiveAsset(SOCIAL_PARSE_RIVE_ASSET_URL).catch(() => {});
    }, [activeTab, showSocialTab]);

    /** True while batch confirm callback is running (all tabs share one CTA pattern). */
    const [batchConfirmInProgress, setBatchConfirmInProgress] = useState(false);

    // ── Place preview modal ───────────────────────────────────────────────────
    const [previewLocation, setPreviewLocation] = useState<Location | null>(null);

    const handlePreviewLocation = useCallback((location: Location): void => {
        setPreviewLocation(location);
    }, []);

    const handleClosePreview = useCallback((): void => {
        setPreviewLocation(null);
    }, []);

    // ── Shared: selected post drill-down ──────────────────────────────────────
    const [selectedPostForLocations, setSelectedPostForLocations] = useState<Post | null>(null);
    const [postLocationsLoading, setPostLocationsLoading] = useState(false);
    const [postLocationsError, setPostLocationsError] = useState('');

    useEffect(() => {
        setDrillDownSelectedKeys(new Set());
    }, [selectedPostForLocations?._id]);

    // ── Fetch My Trips ────────────────────────────────────────────────────────
    useEffect(() => {
        if (activeTab !== 'my-trips' || !user?._id) return;
        let cancelled = false;
        setMyTripsLoading(true);
        setMyTripsError('');
        fetchAllUserPosts(user._id, { page: 1, limit: 50, allowPrivate: true, allowDrafts: true })
            .then((result) => {
                if (!cancelled) setMyTripsPosts(result.posts || []);
            })
            .catch((err) => {
                if (!cancelled) {
                    logError('[PlacePicker] Failed to fetch user posts', err);
                    setMyTripsError('Failed to load your trips. Please try again.');
                }
            })
            .finally(() => {
                if (!cancelled) setMyTripsLoading(false);
            });
        return () => { cancelled = true; };
    }, [activeTab, user?._id]);

    // ── Fetch Saves ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (activeTab !== 'saves' || !user?._id) return;
        let cancelled = false;
        setSavesLoading(true);
        setSavesError('');
        // Saved is everything on /saved: trips & lists (posts) and places saved on their own.
        Promise.all([
            fetchSavedPostsforUser(),
            fetchAllSavedPlaces().catch((err) => {
                logError('[PlacePicker] Failed to fetch saved places', err);
                return [] as Location[];
            }),
        ])
            .then(([posts, places]) => {
                if (cancelled) return;
                setSavesPosts(posts || []);
                setSavedPlaces(places);
                setSavedPlaceSelectedKeys(new Set());
            })
            .catch((err) => {
                if (!cancelled) {
                    logError('[PlacePicker] Failed to fetch saved posts', err);
                    setSavesError('Failed to load your saves. Please try again.');
                }
            })
            .finally(() => {
                if (!cancelled) setSavesLoading(false);
            });
        return () => { cancelled = true; };
    }, [activeTab, user?._id]);

    // ── Search: fetch autocomplete + text search in parallel ─────────────────
    const fetchAutocompleteResults = useCallback(
        async (input: string): Promise<void> => {
            if (abortControllerRef.current) abortControllerRef.current.abort();
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            abortControllerRef.current = new AbortController();

            setSearchLoading(true);
            setSearchHasError(false);
            setSearchErrorMessage('');

            const trimmed = input.trim();
            const cityCoords = getCityCoordinates(city);
            const params: LocationAutocompleteParams = {
                input: trimmed,
                filterMode: 'all',
            };
            if (cityCoords) {
                params.lat = cityCoords.lat;
                params.long = cityCoords.lng;
            }

            const timeoutId = setTimeout(() => {
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                    setSearchLoading(false);
                    setSearchHasError(true);
                    setSearchErrorMessage('Request timed out. Please try again.');
                }
            }, FETCH_TIMEOUT);
            timeoutRef.current = timeoutId;

            WebAnalytics.trackSearch(EVENT_LOCATION_AUTOCOMPLETE_REQUEST, {
                searchText: trimmed,
                city: city?.identifying_name,
                ...params,
            });

            try {
                const [autocompleteResult, textSearchResult] = await Promise.allSettled([
                    getLocationAutocomplete(params),
                    searchLocationByName({
                        locationName: trimmed,
                        city: (city?.identifying_name as string | undefined) || country || undefined,
                    }),
                ]);

                clearTimeout(timeoutId);
                timeoutRef.current = null;

                const autocompleteHits = autocompleteResult.status === 'fulfilled'
                    ? autocompleteResult.value
                    : [];
                const textSearchHits = textSearchResult.status === 'fulfilled'
                    ? textSearchResult.value
                    : [];

                const merged = onlyLocationsInCountry(
                    mergeLocationResults(autocompleteHits, textSearchHits),
                    country
                );

                if (merged.length > 0) {
                    WebAnalytics.trackSearch(EVENT_LOCATION_AUTOCOMPLETE_SUCCESS, {
                        searchText: trimmed,
                        resultCount: merged.length,
                        city: city?.identifying_name,
                    });
                } else {
                    WebAnalytics.trackSearch(EVENT_LOCATION_AUTOCOMPLETE_EMPTY, {
                        searchText: trimmed,
                        city: city?.identifying_name,
                    });
                }

                setSearchResults(merged);
                setSearchRunId((n) => n + 1);
                setSearchLoading(false);
            } catch (error) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
                if (error instanceof Error && error.name === 'AbortError') return;

                WebAnalytics.trackSearch(EVENT_LOCATION_AUTOCOMPLETE_FAILED, {
                    searchText: trimmed,
                    city: city?.identifying_name,
                    errorMessage: error instanceof Error ? error.message : 'Unknown error',
                });

                setSearchLoading(false);
                setSearchHasError(true);
                setSearchErrorMessage(
                    error instanceof Error && error.message?.includes('Network')
                        ? 'Network connection issue. Please check your internet connection and try again.'
                        : 'Failed to fetch results. Please try again.'
                );
            }
        },
        [city, country]
    );

    useEffect(() => {
        if (seededQueryRef.current) return;
        const seed = initialSearchQuery.trim();
        if (!seed) return;
        seededQueryRef.current = true;
        setSearchInput(seed);
        if (seed.length >= MIN_SEARCH_LENGTH) {
            void fetchAutocompleteResults(seed);
            setSearchAttempted(true);
        }
    }, [initialSearchQuery, fetchAutocompleteResults]);

    const debouncedSearch = useCallback(
        (input: string): void => {
            if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = setTimeout(() => {
                if (input.length >= MIN_SEARCH_LENGTH) {
                    void fetchAutocompleteResults(input);
                    setSearchAttempted(true);
                } else if (input.length > 0) {
                    setSearchAttempted(true);
                    setSearchResults([]);
                }
            }, DEBOUNCE_DELAY);
        },
        [fetchAutocompleteResults]
    );

    const exploreRowKey = useCallback(
        (loc: Location, index: number): string => getExploreRowKey(loc, index, searchRunId),
        [searchRunId]
    );

    const handleExploreToggle = useCallback((loc: Location, index: number) => {
        const key = exploreRowKey(loc, index);
        setExploreSelected((prev) => {
            const next = new Map(prev);
            if (next.has(key)) next.delete(key);
            else next.set(key, loc);
            return next;
        });
    }, [exploreRowKey]);

    /** Remove a pending pick from the chip row (those places may not be in the current results). */
    const handleExploreDeselect = useCallback((key: string) => {
        setExploreSelected((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Map(prev);
            next.delete(key);
            return next;
        });
    }, []);

    const runBatchConfirm = useCallback(
        /** Resolves true only when the parent accepted the batch (list/trip handlers throw on failure). */
        async (locations: Location[]): Promise<boolean> => {
            if (!onCopyPasteConfirm || locations.length === 0) return false;
            log('[PlacePicker] runBatchConfirm START', { count: locations.length, names: locations.map(l => l.name) });
            setBatchConfirmInProgress(true);
            try {
                log('[PlacePicker] runBatchConfirm calling onCopyPasteConfirm...');
                await Promise.resolve(onCopyPasteConfirm(locations));
                log('[PlacePicker] runBatchConfirm onCopyPasteConfirm resolved OK');
                return true;
            } catch (err) {
                // Batch handlers (e.g. ListTimelineList) toast before rethrowing; avoid duplicate toasts here.
                logError('[PlacePicker] Batch confirm failed', err);
                return false;
            } finally {
                log('[PlacePicker] runBatchConfirm FINALLY - clearing loading state');
                setBatchConfirmInProgress(false);
            }
        },
        [onCopyPasteConfirm]
    );

    const runSocialDraftConfirm = useCallback(async (): Promise<boolean> => {
        if (!onSocialImportDraftConfirm || !socialDraft || !socialImportSession) return false;
        if (socialDraftSelectedIds.size === 0) return false;
        setBatchConfirmInProgress(true);
        try {
            await onSocialImportDraftConfirm({
                draft: socialDraft,
                selectedClientIds: socialDraftSelectedIds,
                importId: socialImportSession.importId,
                importStartedAt: socialImportSession.importStartedAt,
                sourceType: socialImportSession.sourceType,
            });
            return true;
        } catch (err) {
            logError('[PlacePicker] Social draft confirm failed', err);
            throw err;
        } finally {
            setBatchConfirmInProgress(false);
        }
    }, [
        onSocialImportDraftConfirm,
        socialDraft,
        socialDraftSelectedIds,
        socialImportSession,
    ]);

    const handleExploreBatchConfirm = useCallback(() => {
        if (!onCopyPasteConfirm) return;
        // Straight from the accumulator: earlier picks are no longer in `searchResults`.
        const selected = Array.from(exploreSelected.values());
        if (selected.length === 0) return;
        void runBatchConfirm(selected).then((added) => {
            // Keep the selection on failure — the parent leaves the modal open for retry.
            if (added) setExploreSelected(new Map());
        });
    }, [exploreSelected, onCopyPasteConfirm, runBatchConfirm]);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>): void => {
            const value = e.target.value;
            setSearchInput(value);
            setFocusedResultIndex(-1);
            if (value.length === 0) {
                if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
                setSearchAttempted(false);
                setSearchResults([]);
                setSearchHasError(false);
                setSearchErrorMessage('');
            } else {
                debouncedSearch(value);
            }
        },
        [debouncedSearch]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>): void => {
            if (searchResults.length === 0) return;
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setFocusedResultIndex((prev) =>
                        prev < searchResults.length - 1 ? prev + 1 : 0
                    );
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setFocusedResultIndex((prev) =>
                        prev > 0 ? prev - 1 : searchResults.length - 1
                    );
                    break;
                case 'Enter':
                    if (focusedResultIndex >= 0 && focusedResultIndex < searchResults.length) {
                        e.preventDefault();
                        const loc = searchResults[focusedResultIndex];
                        if (exploreBatchMode) {
                            handleExploreToggle(loc, focusedResultIndex);
                        } else {
                            onSelectPlace(loc, 'search');
                        }
                    }
                    break;
                case 'Escape':
                    break;
            }
        },
        [searchResults, focusedResultIndex, onSelectPlace, exploreBatchMode, handleExploreToggle]
    );

    // ── Post drill-down ───────────────────────────────────────────────────────
    const handlePostClick = useCallback(async (post: Post) => {
        setPostLocationsError('');
        setPostLocationsLoading(true);
        try {
            const fullPost = await getPostById(post._id);
            setSelectedPostForLocations(fullPost);
        } catch (err) {
            logError('[PlacePicker] Failed to fetch post locations', err);
            setPostLocationsError('Failed to load places for this post. Please try again.');
        } finally {
            setPostLocationsLoading(false);
        }
    }, []);

    const handleBackToPostGrid = useCallback(() => {
        setSelectedPostForLocations(null);
        setPostLocationsError('');
    }, []);

    // ── Tab switch: clear drill-down ──────────────────────────────────────────
    const handleTabChange = useCallback((tab: PlacePickerTab) => {
        setActiveTab(tab);
        setSelectedPostForLocations(null);
        setPostLocationsError('');
    }, []);

    const handleCopyPasteParse = useCallback(async () => {
        const body = copyPasteText.trim();
        if (!body) {
            setCopyPasteError('Paste a list of places first.');
            return;
        }
        setCopyPasteLoading(true);
        setCopyPasteError('');
        try {
            const locations = dedupeLocations(await parseLocationsFromText({ title: '', body }));
            if (!locations.length) {
                setCopyPasteError('No places found. Try adding city names or more detail.');
                setCopyPasteResults([]);
                setCopyPasteSelectedKeys(new Set());
                return;
            }
            setCopyPasteResults(locations);
            setCopyPasteSelectedKeys(new Set());
            setCopyPasteControlsCollapsed(true);
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Failed to parse places.';
            setCopyPasteError(msg);
            toast.error(msg);
        } finally {
            setCopyPasteLoading(false);
        }
    }, [copyPasteText]);

    const handleCopyPasteToggle = useCallback((loc: Location, index: number) => {
        const key = getResultRowKey(loc, index);
        setCopyPasteSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const handleCopyPasteAddSelected = useCallback(() => {
        if (!onCopyPasteConfirm) return;
        const selected = copyPasteResults.filter((loc, i) =>
            copyPasteSelectedKeys.has(getResultRowKey(loc, i))
        );
        if (selected.length === 0) return;
        void runBatchConfirm(selected);
    }, [copyPasteResults, copyPasteSelectedKeys, onCopyPasteConfirm, runBatchConfirm]);

    const handleSocialImport = useCallback(async () => {
        const trimmed = socialLink.trim();
        if (!trimmed) {
            setSocialError('Paste a link to your post.');
            return;
        }
        const validation = validateSocialMediaUrl(trimmed);
        if (!validation.isValid) {
            setSocialError(validation.error || 'Please enter a valid Instagram or TikTok URL.');
            return;
        }
        const normalized = validation.normalizedUrl ?? trimmed;
        setSocialLoading(true);
        setSocialError('');
        WebAnalytics.track(EVENT_INSPO_LINK_SUBMITTED, {
            link_url: normalized,
            platform: detectSocialPlatform(normalized),
        });
        try {
            if (onSocialImportDraftConfirm) {
                const source = buildSocialItineraryImportSource(normalized);
                if (!source) {
                    setSocialError('Please enter a valid Instagram or TikTok URL.');
                    return;
                }
                const importId =
                    typeof crypto !== 'undefined' && 'randomUUID' in crypto
                        ? crypto.randomUUID()
                        : `import-${Date.now()}`;
                const importStartedAt = new Date().toISOString();
                const draft = await requestItineraryImport({
                    source,
                    import_id: importId,
                    import_started_at: importStartedAt,
                });
                const selected = defaultSelectedPlaceIds(draft);
                if (selected.size === 0) {
                    setSocialError('No places found in this post. Try another link.');
                    setSocialDraft(null);
                    setSocialDraftSelectedIds(new Set());
                    return;
                }
                setSocialDraft(draft);
                setSocialDraftSelectedIds(selected);
                setSocialImportSession({
                    importId,
                    importStartedAt,
                    sourceType: source.type,
                });
                setSocialResults([]);
                setSocialSelectedKeys(new Set());
                setSocialControlsCollapsed(true);
                return;
            }

            const response = await socialScraperService.importFromSocial(normalized, false);
            const withPlaces = response.locations.filter((row) => row.place !== null);
            const locations: Location[] = [];
            for (let i = 0; i < withPlaces.length; i += 1) {
                const loc = socialScraperEntryToLocation(withPlaces[i]);
                if (loc) locations.push(loc);
            }
            if (locations.length === 0) {
                setSocialError('No locations found in this post. Try another link.');
                setSocialResults([]);
                setSocialSelectedKeys(new Set());
                return;
            }
            setSocialDraft(null);
            setSocialResults(locations);
            setSocialSelectedKeys(new Set());
            setSocialControlsCollapsed(true);
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Failed to import from this link.';
            setSocialError(msg);
            toast.error(msg);
        } finally {
            setSocialLoading(false);
        }
    }, [onSocialImportDraftConfirm, socialLink]);

    const handleSocialToggle = useCallback((loc: Location, index: number) => {
        const key = getSocialBatchRowKey(loc, index);
        setSocialSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const handleSocialAddSelected = useCallback(() => {
        if (socialDraft && socialImportSession && onSocialImportDraftConfirm) {
            if (socialDraftSelectedIds.size === 0) return;
            void runSocialDraftConfirm();
            return;
        }
        if (!onCopyPasteConfirm) return;
        const selected = socialResults.filter((loc, i) =>
            socialSelectedKeys.has(getSocialBatchRowKey(loc, i))
        );
        if (selected.length === 0) return;
        void runBatchConfirm(selected);
    }, [
        onCopyPasteConfirm,
        runSocialDraftConfirm,
        socialDraft,
        socialDraftSelectedIds,
        socialImportSession,
        socialResults,
        socialSelectedKeys,
    ]);

    const handleSocialDraftToggle = useCallback((clientId: string) => {
        setSocialDraftSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(clientId)) next.delete(clientId);
            else next.add(clientId);
            return next;
        });
    }, []);

    const socialDraftPlaces = useMemo((): ItineraryImportDraftPlace[] => {
        if (!socialDraft) return [];
        return socialDraft.days.flatMap((day) => day.places);
    }, [socialDraft]);

    const handleDrillDownToggle = useCallback((rowKey: string) => {
        setDrillDownSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    }, []);

    const handleDrillDownBatchConfirm = useCallback(() => {
        if (!onCopyPasteConfirm || !selectedPostForLocations) return;
        const selected: Location[] = [];
        (selectedPostForLocations.postLocations || []).forEach((pl, idx) => {
            const loc = pl.location;
            const hasIdentity =
                loc &&
                ((typeof loc._id === 'string' && loc._id.length > 0) ||
                    (typeof loc.google_place_id === 'string' && loc.google_place_id.length > 0));
            if (!hasIdentity) return;
            const mergedLocation = Object.assign(
                Object.create(Object.getPrototypeOf(loc)) as Location,
                loc,
                pl.image_urls?.length ? { image_urls: pl.image_urls } : {}
            );
            const key = getDrillDownRowKey(pl, mergedLocation, idx);
            if (drillDownSelectedKeys.has(key)) selected.push(mergedLocation);
        });
        if (selected.length === 0) return;
        void runBatchConfirm(selected);
    }, [selectedPostForLocations, drillDownSelectedKeys, onCopyPasteConfirm, runBatchConfirm]);

    const handleSavedPlaceToggle = useCallback((rowKey: string) => {
        setSavedPlaceSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    }, []);

    const handleSavedPlacesBatchConfirm = useCallback(() => {
        if (!onCopyPasteConfirm) return;
        const selected = savedPlaces.filter((loc, i) =>
            savedPlaceSelectedKeys.has(getSavedPlaceRowKey(loc, i))
        );
        if (selected.length === 0) return;
        void runBatchConfirm(selected);
    }, [savedPlaces, savedPlaceSelectedKeys, onCopyPasteConfirm, runBatchConfirm]);

    useEffect(() => {
        if (!showCopyPasteTab && activeTab === 'copy-paste') {
            setActiveTab('saves');
        }
        if (!showSocialTab && activeTab === 'social') {
            setActiveTab('saves');
        }
    }, [showCopyPasteTab, showSocialTab, activeTab]);

    const tabDefinitions = useMemo(() => {
        const t: { id: PlacePickerTab; label: string }[] = [{ id: 'saves', label: 'Saved' }];
        if (showSocialTab) {
            t.push({ id: 'social', label: 'Social' });
        }
        t.push({ id: 'search', label: searchTabLabel });
        if (showCopyPasteTab) {
            t.push({ id: 'copy-paste', label: 'Copy & Paste' });
        }
        t.push({ id: 'my-trips', label: 'My Trips' });
        return t;
    }, [searchTabLabel, showCopyPasteTab, showSocialTab]);

    // ── Cleanup ───────────────────────────────────────────────────────────────
    useEffect(() => () => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    }, []);

    // ── Reset focused index when results change ───────────────────────────────
    useEffect(() => {
        if (focusedResultIndex >= searchResults.length) {
            setFocusedResultIndex(-1);
        }
    }, [searchResults.length, focusedResultIndex]);

    // ── Render: post grid ─────────────────────────────────────────────────────
    const renderPostGrid = (
        posts: Post[],
        loading: boolean,
        error: string,
        emptyMessage: string,
        tabName: string
    ) => {
        if (postLocationsLoading) {
            return <div className="place-picker-loading"><LoadingSpinner /></div>;
        }
        if (postLocationsError) {
            return (
                <div className="place-picker-error-row">
                    <p className="place-picker-error">{postLocationsError}</p>
                    <button className="place-picker-back-btn" onClick={handleBackToPostGrid}>← Back</button>
                </div>
            );
        }
        if (selectedPostForLocations) {
            return renderLocationList(selectedPostForLocations);
        }
        if (loading) return <div className="place-picker-loading"><LoadingSpinner /></div>;
        if (error) return <p className="place-picker-error">{error}</p>;
        if (!user) return <p className="place-picker-empty">Sign in to see your {tabName}.</p>;
        if (posts.length === 0) return <p className="place-picker-empty">{emptyMessage}</p>;

        return (
            <div className="place-picker-post-list" role="list">
                {posts.map((post) => {
                    const images = getPostImages(post);
                    const [primaryImage, ...fallbackImages] = images;
                    const subtitle = getPostSubtitle(post);
                    const title = post.name || 'Untitled';
                    return (
                        <button
                            key={post._id}
                            type="button"
                            className="place-picker-post-row"
                            onClick={() => void handlePostClick(post)}
                            aria-label={`See inside ${title}`}
                        >
                            <div className="place-picker-post-row-thumb">
                                <CustomImage
                                    src={primaryImage}
                                    fallbackSources={fallbackImages}
                                    fallbackSrc={DEFAULT_POST_IMAGE}
                                    alt=""
                                    fill
                                    sizes="56px"
                                    lazy
                                    style={{
                                        objectFit: 'cover',
                                        objectPosition: 'center',
                                        borderRadius: 8,
                                    }}
                                />
                            </div>
                            <div className="place-picker-post-row-body">
                                <span className="place-picker-post-row-name">{title}</span>
                                {subtitle ? (
                                    <span className="place-picker-post-row-sub">{subtitle}</span>
                                ) : null}
                            </div>
                            <span className="place-picker-post-row-action" aria-hidden>
                                <span className="place-picker-post-row-action-label">See inside</span>
                                <ChevronRightIcon
                                    className="place-picker-post-row-chevron"
                                    sx={{ fontSize: 22, color: '#525257' }}
                                />
                            </span>
                        </button>
                    );
                })}
            </div>
        );
    };

    // ── Render: Saved tab (saved places, then saved trips & lists) ────────────
    const renderSavesTab = () => {
        // An opened trip / list (or one loading) takes over the tab, as before.
        if (selectedPostForLocations || postLocationsLoading || postLocationsError) {
            return renderPostGrid(savesPosts, false, '', '', 'saved posts');
        }
        if (savesLoading) return <div className="place-picker-loading"><LoadingSpinner /></div>;
        if (savesError) return <p className="place-picker-error">{savesError}</p>;
        if (!user) return <p className="place-picker-empty">Sign in to see your saves.</p>;
        if (savedPlaces.length === 0 && savesPosts.length === 0) {
            return <p className="place-picker-empty">You haven't saved anything yet.</p>;
        }

        const sections = (
            <>
                {savedPlaces.length > 0 ? (
                    <section className="place-picker-saves-section" aria-label="Saved places">
                        <h3 className="place-picker-saves-heading">Places · {savedPlaces.length}</h3>
                        <div className="place-picker-location-grid">
                            {savedPlaces.map((loc, idx) => {
                                const rowKey = getSavedPlaceRowKey(loc, idx);
                                return (
                                    <PlaceSearchResultCard
                                        key={rowKey}
                                        location={loc}
                                        selectable={batchSelectionEnabled}
                                        isSelected={savedPlaceSelectedKeys.has(rowKey)}
                                        onAdd={
                                            batchSelectionEnabled
                                                ? () => handleSavedPlaceToggle(rowKey)
                                                : (l) => onSelectPlace(l, 'saves')
                                        }
                                        onPreview={handlePreviewLocation}
                                    />
                                );
                            })}
                        </div>
                    </section>
                ) : null}
                {savesPosts.length > 0 ? (
                    <section className="place-picker-saves-section" aria-label="Saved trips and lists">
                        <h3 className="place-picker-saves-heading">Trips &amp; Lists · {savesPosts.length}</h3>
                        {renderPostGrid(savesPosts, false, '', '', 'saved posts')}
                    </section>
                ) : null}
            </>
        );

        if (!batchSelectionEnabled || savedPlaces.length === 0) {
            return <div className="place-picker-saves">{sections}</div>;
        }

        const n = savedPlaceSelectedKeys.size;
        const { label, ariaLabel } = getBatchAddLabels(n, batchDestination);
        return (
            <div className="place-picker-location-grid-view place-picker-location-grid-view--batch">
                <PlacePickerBatchListSection
                    scrollKey={savedPlaces.length + savesPosts.length}
                    hint={
                        <p className="place-picker-copy-paste-hint">
                            {getBatchSelectHint(batchDestination)}
                        </p>
                    }
                    list={<div className="place-picker-saves">{sections}</div>}
                    footer={
                        <Button
                            variant="primary"
                            type="button"
                            className="place-picker-batch-confirm-btn"
                            fullWidth
                            disabled={n === 0}
                            loading={batchConfirmInProgress}
                            onClick={handleSavedPlacesBatchConfirm}
                            ariaLabel={ariaLabel}
                        >
                            {label}
                        </Button>
                    }
                />
            </div>
        );
    };

    // ── Render: location grid (drill-down from a post) ────────────────────────
    const renderLocationList = (post: Post) => {
        const locations: PostLocation[] = post.postLocations || [];
        const drillDownSource: PlacePickerSelectSource =
            activeTab === 'my-trips' ? 'my-trips' : activeTab === 'saves' ? 'saves' : 'search';
        const locationRows = (
            <div className="place-picker-location-grid">
                {locations.map((pl, idx) => {
                    const loc = pl.location;
                    const locHasIdentity =
                        loc &&
                        ((typeof loc._id === 'string' && loc._id.length > 0) ||
                            (typeof loc.google_place_id === 'string' &&
                                loc.google_place_id.length > 0));
                    if (!locHasIdentity) return null;

                    const mergedLocation = Object.assign(
                        Object.create(Object.getPrototypeOf(loc)) as Location,
                        loc,
                        pl.image_urls?.length ? { image_urls: pl.image_urls } : {}
                    );
                    const rowKey = getDrillDownRowKey(pl, mergedLocation, idx);
                    const isSelected = drillDownSelectedKeys.has(rowKey);

                    return (
                        <PlaceSearchResultCard
                            key={rowKey}
                            location={mergedLocation}
                            selectable={batchSelectionEnabled}
                            isSelected={isSelected}
                            onAdd={
                                batchSelectionEnabled
                                    ? () => handleDrillDownToggle(rowKey)
                                    : (l) => onSelectPlace(l, drillDownSource)
                            }
                            onPreview={handlePreviewLocation}
                        />
                    );
                })}
            </div>
        );

        return (
            <div
                className={`place-picker-location-grid-view${
                    batchSelectionEnabled && locations.length > 0
                        ? ' place-picker-location-grid-view--batch'
                        : ''
                }`}
            >
                <div className="place-picker-location-list-header">
                    <button className="place-picker-back-btn" onClick={handleBackToPostGrid}>
                        ← Back
                    </button>
                    <span className="place-picker-location-list-title">{post.name || 'Post'}</span>
                </div>
                {locations.length === 0 ? (
                    <p className="place-picker-empty">No places in this post.</p>
                ) : batchSelectionEnabled ? (
                    <PlacePickerBatchListSection
                        scrollKey={locations.length}
                        hint={
                            <p className="place-picker-copy-paste-hint">
                                {getBatchSelectHint(batchDestination)}
                            </p>
                        }
                        list={locationRows}
                        footer={(() => {
                            const n = drillDownSelectedKeys.size;
                            const { label, ariaLabel } = getBatchAddLabels(n, batchDestination);
                            return (
                                <Button
                                    variant="primary"
                                    type="button"
                                    className="place-picker-batch-confirm-btn"
                                    fullWidth
                                    disabled={n === 0}
                                    loading={batchConfirmInProgress}
                                    onClick={handleDrillDownBatchConfirm}
                                    ariaLabel={ariaLabel}
                                >
                                    {label}
                                </Button>
                            );
                        })()}
                    />
                ) : (
                    locationRows
                )}
            </div>
        );
    };

    // ── Render: Copy & Paste tab ────────────────────────────────────────────────
    const renderCopyPasteTab = () => (
        <div className="place-picker-copy-paste-tab">
            {copyPasteResults.length > 0 ? (
                <button
                    type="button"
                    className="place-picker-controls-toggle"
                    onClick={() => setCopyPasteControlsCollapsed((prev) => !prev)}
                    aria-expanded={!copyPasteControlsCollapsed}
                >
                    <span className="place-picker-controls-toggle-label">Paste places</span>
                    <span className="place-picker-controls-toggle-chevron" aria-hidden>
                        {copyPasteControlsCollapsed ? '▼' : '▲'}
                    </span>
                </button>
            ) : null}
            {!copyPasteControlsCollapsed && (
                <>
                    <textarea
                        className="place-picker-copy-paste-textarea"
                        value={copyPasteText}
                        onChange={(e) => {
                            setCopyPasteText(e.target.value);
                            setCopyPasteError('');
                        }}
                        placeholder="Paste places, a caption, or a list and AI will add them for you"
                        rows={6}
                        autoCapitalize="none"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        disabled={copyPasteLoading}
                        aria-label="Paste places, a caption, or a list and AI will add them for you"
                    />
                    {copyPasteError ? (
                        <p className="place-picker-copy-paste-error" role="alert">
                            {copyPasteError}
                        </p>
                    ) : null}
                    <div className="place-picker-copy-paste-actions">
                        <Button
                            variant="primary"
                            type="button"
                            onClick={() => void handleCopyPasteParse()}
                            disabled={copyPasteLoading || !copyPasteText.trim()}
                            loading={copyPasteLoading}
                        >
                            {copyPasteLoading ? 'Finding places…' : 'Find places'}
                        </Button>
                    </div>
                </>
            )}
            {copyPasteResults.length > 0 ? (
                onCopyPasteConfirm ? (
                    <PlacePickerBatchListSection
                        scrollKey={copyPasteResults.length}
                        hint={<p className="place-picker-copy-paste-hint">{getBatchSelectHint(batchDestination)}</p>}
                        list={
                            <div className="place-search-results-list" role="list">
                                {copyPasteResults.map((location, index) => {
                                    const rowKey = getResultRowKey(location, index);
                                    const isSelected = copyPasteSelectedKeys.has(rowKey);
                                    return (
                                        <PlaceSearchResultCard
                                            key={rowKey}
                                            location={location}
                                            selectable
                                            isSelected={isSelected}
                                            onAdd={() => handleCopyPasteToggle(location, index)}
                                            onPreview={handlePreviewLocation}
                                        />
                                    );
                                })}
                            </div>
                        }
                        footer={(() => {
                            const n = copyPasteSelectedKeys.size;
                            const { label, ariaLabel } = getBatchAddLabels(n, batchDestination);
                            return (
                                <Button
                                    variant="primary"
                                    type="button"
                                    className="place-picker-batch-confirm-btn"
                                    fullWidth
                                    disabled={n === 0}
                                    loading={batchConfirmInProgress}
                                    onClick={handleCopyPasteAddSelected}
                                    ariaLabel={ariaLabel}
                                >
                                    {label}
                                </Button>
                            );
                        })()}
                    />
                ) : (
                    <div className="place-search-results place-picker-copy-paste-results">
                        <div className="place-search-results-list" role="list">
                            {copyPasteResults.map((location, index) => {
                                const rowKey = getResultRowKey(location, index);
                                const isSelected = copyPasteSelectedKeys.has(rowKey);
                                return (
                                    <PlaceSearchResultCard
                                        key={rowKey}
                                        location={location}
                                        selectable
                                        isSelected={isSelected}
                                        onAdd={() => handleCopyPasteToggle(location, index)}
                                        onPreview={handlePreviewLocation}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )
            ) : null}
        </div>
    );

    // ── Render: Social tab (Inspo scraper) ─────────────────────────────────────
    const hasSocialBatchResults = socialResults.length > 0 || socialDraftPlaces.length > 0;

    const renderSocialTab = () => (socialLoading ? (
        <div className="place-picker-social-tab" role="status" aria-live="polite" aria-label="Finding places">
            <SocialParseAnimation className="place-picker-social-parse-animation" visibleFraction={0.55} />
        </div>
    ) : (
        <div className="place-picker-social-tab">
            {hasSocialBatchResults ? (
                <button
                    type="button"
                    className="place-picker-controls-toggle"
                    onClick={() => setSocialControlsCollapsed((prev) => !prev)}
                    aria-expanded={!socialControlsCollapsed}
                >
                    <span className="place-picker-controls-toggle-label">Paste social link</span>
                    <span className="place-picker-controls-toggle-chevron" aria-hidden>
                        {socialControlsCollapsed ? '▼' : '▲'}
                    </span>
                </button>
            ) : null}
            {!socialControlsCollapsed && (
                <>
                    <p className="place-picker-social-intro">
                        Paste your Instagram or TikTok link. AI pulls place suggestions from the post; you pick
                        what to add to your trip or list.
                    </p>
                    <div className="place-picker-social-link-row">
                        <span className="place-picker-social-icons" aria-hidden>
                            <SVGTikTokIcon size={28} />
                            <SVGInstagramIcon size={28} />
                        </span>
                        <input
                            className="place-picker-social-input"
                            type="url"
                            inputMode="url"
                            placeholder="Paste link"
                            value={socialLink}
                            onChange={(e) => {
                                setSocialLink(e.target.value);
                                setSocialError('');
                            }}
                            disabled={socialLoading}
                            autoComplete="off"
                            aria-label="Instagram or TikTok post URL"
                        />
                    </div>
                    {socialError ? (
                        <p className="place-picker-copy-paste-error" role="alert">
                            {socialError}
                        </p>
                    ) : null}
                    <div className="place-picker-copy-paste-actions">
                        <Button
                            variant="primary"
                            type="button"
                            onClick={() => void handleSocialImport()}
                            disabled={socialLoading || !socialLink.trim()}
                            loading={socialLoading}
                        >
                            {socialLoading ? 'Finding places…' : 'Find places'}
                        </Button>
                    </div>
                </>
            )}
            {hasSocialBatchResults ? (
                socialDraftPlaces.length > 0 && onSocialImportDraftConfirm ? (
                    <PlacePickerBatchListSection
                        scrollKey={socialDraftPlaces.length}
                        hint={<p className="place-picker-copy-paste-hint">{getBatchSelectHint(batchDestination)}</p>}
                        list={
                            <div className="place-search-results-list" role="list">
                                {socialDraftPlaces.map((place) => {
                                    if (isTravelImportPlace(place)) return null;
                                    const review = mapDraftPlaceToReviewPlace(place, 'itinerary', {
                                        notesInExpandedPanel: true,
                                    });
                                    const isSelected = socialDraftSelectedIds.has(place.client_id);
                                    return (
                                        <button
                                            key={place.client_id}
                                            type="button"
                                            className={`place-search-result-card place-search-result-card--selectable${
                                                isSelected ? ' place-search-result-card--selected' : ''
                                            }`}
                                            onClick={() => handleSocialDraftToggle(place.client_id)}
                                        >
                                            <span className="place-search-result-card-select" aria-hidden>
                                                {isSelected ? '✓' : ''}
                                            </span>
                                            {review.imageUrl ? (
                                                <CustomImage
                                                    src={review.imageUrl}
                                                    alt=""
                                                    className="place-search-result-card-image"
                                                    width={48}
                                                    height={48}
                                                />
                                            ) : (
                                                <PlaceSearchRowTypeIcon kind={review.iconKind} />
                                            )}
                                            <span className="place-search-result-card-text">
                                                <span className="place-search-result-card-title">{review.title}</span>
                                                {review.subtitle ? (
                                                    <span className="place-search-result-card-subtitle">
                                                        {review.subtitle}
                                                    </span>
                                                ) : null}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        }
                        footer={(() => {
                            const n = socialDraftSelectedIds.size;
                            const { label, ariaLabel } = getBatchAddLabels(n, batchDestination);
                            return (
                                <Button
                                    variant="primary"
                                    type="button"
                                    className="place-picker-batch-confirm-btn"
                                    fullWidth
                                    disabled={n === 0}
                                    loading={batchConfirmInProgress}
                                    onClick={handleSocialAddSelected}
                                    ariaLabel={ariaLabel}
                                >
                                    {label}
                                </Button>
                            );
                        })()}
                    />
                ) : onCopyPasteConfirm ? (
                    <PlacePickerBatchListSection
                        scrollKey={socialResults.length}
                        hint={<p className="place-picker-copy-paste-hint">{getBatchSelectHint(batchDestination)}</p>}
                        list={
                            <div className="place-search-results-list" role="list">
                                {socialResults.map((location, index) => {
                                    const rowKey = getSocialBatchRowKey(location, index);
                                    const isSelected = socialSelectedKeys.has(rowKey);
                                    return (
                                        <PlaceSearchResultCard
                                            key={rowKey}
                                            location={location}
                                            selectable
                                            isSelected={isSelected}
                                            onAdd={() => handleSocialToggle(location, index)}
                                            onPreview={handlePreviewLocation}
                                        />
                                    );
                                })}
                            </div>
                        }
                        footer={(() => {
                            const n = socialSelectedKeys.size;
                            const { label, ariaLabel } = getBatchAddLabels(n, batchDestination);
                            return (
                                <Button
                                    variant="primary"
                                    type="button"
                                    className="place-picker-batch-confirm-btn"
                                    fullWidth
                                    disabled={n === 0}
                                    loading={batchConfirmInProgress}
                                    onClick={handleSocialAddSelected}
                                    ariaLabel={ariaLabel}
                                >
                                    {label}
                                </Button>
                            );
                        })()}
                    />
                ) : (
                    <div className="place-search-results place-picker-copy-paste-results">
                        <div className="place-search-results-list" role="list">
                            {socialResults.map((location, index) => {
                                const rowKey = getSocialBatchRowKey(location, index);
                                const isSelected = socialSelectedKeys.has(rowKey);
                                return (
                                    <PlaceSearchResultCard
                                        key={rowKey}
                                        location={location}
                                        selectable
                                        isSelected={isSelected}
                                        onAdd={() => handleSocialToggle(location, index)}
                                        onPreview={handlePreviewLocation}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )
            ) : null}
        </div>
    ));

    // ── Render: search tab ────────────────────────────────────────────────────
    const renderSearchTab = () => (
        <div className="place-picker-search-tab">
            <div className="place-picker-search-input-row">
                <input
                    className="place-picker-search-input"
                    type="text"
                    placeholder="Search places"
                    value={searchInput}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    // On mobile, autofocus pops the keyboard as soon as the sheet opens (incl. the
                    // empty-trip auto-open) and the browser scrolls the page to the field.
                    autoFocus={!isMobileClient()}
                    autoComplete="off"
                    aria-label="Search for places"
                />
                {searchLoading && <div className="place-picker-search-spinner" aria-hidden="true" />}
            </div>

            {/* Error */}
            {searchHasError && !searchLoading && (
                <div className="place-picker-search-error">
                    <p>{searchErrorMessage}</p>
                    <button
                        className="place-picker-retry-btn"
                        onClick={() => {
                            if (searchInput.length >= MIN_SEARCH_LENGTH) {
                                void fetchAutocompleteResults(searchInput);
                            }
                        }}
                    >
                        Try Again
                    </button>
                </div>
            )}

            {/* Explore batch: stays mounted while anything is selected — including after the box is
                cleared or a query returns nothing — so the picks and the CTA never go out of reach.
                `searchLoading` is deliberately not gated on here: collapsing mid-debounce would flap
                the layout between the results and the chips-only view on every keystroke. */}
            {exploreBatchMode && !searchHasError && (searchResults.length > 0 || exploreSelected.size > 0) && (
                <PlacePickerBatchListSection
                    scrollKey={`${searchInput}:${searchResults.length}:${exploreSelected.size}`}
                    hint={
                        <>
                            {exploreSelected.size > 0 && (
                                <ul
                                    className="place-picker-explore-chips"
                                    aria-label={`Selected places (${exploreSelected.size})`}
                                >
                                    {Array.from(exploreSelected.entries()).map(([key, loc]) => (
                                        <li key={key} className="place-picker-explore-chip">
                                            <span className="place-picker-explore-chip-label">
                                                {loc.name || 'Unnamed place'}
                                            </span>
                                            <button
                                                type="button"
                                                className="place-picker-explore-chip-remove"
                                                onClick={() => handleExploreDeselect(key)}
                                                aria-label={`Remove ${loc.name || 'place'} from selection`}
                                            >
                                                ×
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {/* Once places are picked the chips + CTA say everything the instructional
                                hint did, so drop it and give the row back to the results. */}
                            {exploreSelected.size === 0 ? (
                                <p className="place-picker-copy-paste-hint">
                                    {getBatchSelectHint(batchDestination)}
                                </p>
                            ) : searchResults.length === 0 ? (
                                <p className="place-picker-copy-paste-hint">
                                    {getExplorePendingHint(exploreSelected.size)}
                                </p>
                            ) : null}
                        </>
                    }
                    list={
                        searchResults.length > 0 ? (
                            <PlaceSearchResults
                                embedded
                                results={searchResults}
                                onPlaceSelect={(loc, index) => handleExploreToggle(loc, index)}
                                onPlacePreview={handlePreviewLocation}
                                isAdding={false}
                                focusedIndex={focusedResultIndex}
                                selectable
                                selectedKeys={exploreSelectedKeys}
                                getRowKey={exploreRowKey}
                            />
                        ) : null
                    }
                    footer={(() => {
                        const n = exploreSelected.size;
                        const { label, ariaLabel } = getBatchAddLabels(n, batchDestination);
                        return (
                            <Button
                                variant="primary"
                                type="button"
                                className="place-picker-batch-confirm-btn"
                                fullWidth
                                disabled={n === 0}
                                loading={batchConfirmInProgress}
                                onClick={handleExploreBatchConfirm}
                                ariaLabel={ariaLabel}
                            >
                                {label}
                            </Button>
                        );
                    })()}
                />
            )}

            {/* Single-select Explore (place page / recommend flows) */}
            {!exploreBatchMode && !searchLoading && !searchHasError && searchResults.length > 0 && (
                <PlaceSearchResults
                    results={searchResults}
                    onPlaceSelect={(loc, _index) => onSelectPlace(loc, 'search')}
                    // List Explore: row tap should add (not open preview). Itinerary keeps preview-on-row.
                    onPlacePreview={
                        contextLabel === 'list' ? undefined : handlePreviewLocation
                    }
                    isAdding={false}
                    focusedIndex={focusedResultIndex}
                />
            )}

            {/* Empty states */}
            {!searchLoading && !searchHasError && searchAttempted && searchResults.length === 0 && (
                <div className="place-picker-search-empty">
                    {searchInput.length < MIN_SEARCH_LENGTH ? (
                        <>
                            <p className="place-picker-search-empty-title">Keep typing...</p>
                            <p className="place-picker-search-empty-message">
                                Please enter at least {MIN_SEARCH_LENGTH} characters to search
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="place-picker-search-empty-title">No results found</p>
                            <p className="place-picker-search-empty-message">
                                We couldn&apos;t find any places matching &quot;{searchInput}&quot;
                            </p>
                        </>
                    )}
                </div>
            )}

            {/* Initial state — suppressed while Explore picks are pending so the chips + CTA own the space */}
            {!searchLoading && !searchHasError && !searchAttempted && exploreSelected.size === 0 && (
                <div className="place-picker-search-initial">
                    <p className="place-picker-search-initial-title">Search for places</p>
                    <p className="place-picker-search-initial-message">
                        {contextLabel
                            ? `Search for restaurants, attractions, hotels, and more to add to your ${contextLabel}.`
                            : 'Search for restaurants, attractions, hotels, and more.'}
                        <br />
                        <span className="place-picker-search-initial-tip">
                            Tip: Include city or location names for better results
                        </span>
                    </p>
                    {onCustomCardClick && (
                        <div className="place-picker-custom-section">
                            <span className="place-picker-custom-text">
                                Can&apos;t find your place?
                            </span>
                            <button
                                type="button"
                                onClick={onCustomCardClick}
                                className="place-picker-custom-link"
                            >
                                Add custom place.
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className={`place-picker ${className}`}>
            {previewLocation && (
                <CombinedContentPlaceModal
                    isOpen
                    onClose={handleClosePreview}
                    locationId={previewLocation._id || undefined}
                    locationData={previewLocation as unknown as Partial<LocationModel>}
                />
            )}
            {/* Tab pills */}
            {!searchOnly ? <div className="place-picker-tabs" role="tablist">
                {tabDefinitions.map((tab) => (
                    <button
                        key={tab.id}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={`place-picker-tab${activeTab === tab.id ? ' place-picker-tab--active' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div> : null}

            {/* Tab content (order matches pills: Saved, Social, Explore, Copy & Paste, My Trips) */}
            <div className="place-picker-tab-content">
                {activeTab === 'saves' && renderSavesTab()}
                {activeTab === 'social' && showSocialTab && renderSocialTab()}
                {activeTab === 'search' && renderSearchTab()}
                {activeTab === 'copy-paste' && showCopyPasteTab && renderCopyPasteTab()}
                {activeTab === 'my-trips' && renderPostGrid(
                    myTripsPosts,
                    myTripsLoading,
                    myTripsError,
                    "You haven't created any trips yet.",
                    'trips'
                )}
            </div>
        </div>
    );
};

export default PlacePicker;
